/**
 * Permission constants - Update these to match your API's permission strings
 */
export const PERMISSIONS = {
  // Dashboard
  VIEW_DASHBOARD: 'view_dashboard',
  
  // User Management
  VIEW_USERS: 'view_users',
  CREATE_USERS: 'create_users',
  EDIT_USERS: 'edit_users',
  DELETE_USERS: 'delete_users',
  
  // Role Management
  VIEW_ROLES: 'view_roles',
  CREATE_ROLES: 'create_roles',
  EDIT_ROLES: 'edit_roles',
  DELETE_ROLES: 'delete_roles',
  MANAGE_PERMISSIONS: 'manage_permissions',
  
  // Product Management
  VIEW_PRODUCTS: 'view_products',
  CREATE_PRODUCTS: 'create_products',
  EDIT_PRODUCTS: 'edit_products',
  DELETE_PRODUCTS: 'delete_products',
  
  // Category Management
  VIEW_CATEGORIES: 'view_categories',
  CREATE_CATEGORIES: 'create_categories',
  EDIT_CATEGORIES: 'edit_categories',
  DELETE_CATEGORIES: 'delete_categories',
  
  // Order Management
  VIEW_ORDERS: 'view_orders',
  CREATE_ORDERS: 'create_orders',
  EDIT_ORDERS: 'edit_orders',
  DELETE_ORDERS: 'delete_orders',
  
  // Invoice Management
  VIEW_INVOICES: 'view_invoices',
  CREATE_INVOICES: 'create_invoices',
  EDIT_INVOICES: 'edit_invoices',
  DELETE_INVOICES: 'delete_invoices',
  
  // Inventory Management
  VIEW_INVENTORY: 'view_inventory',
  MANAGE_INVENTORY: 'manage_inventory',
  
  // Purchase Orders
  VIEW_PURCHASE_ORDERS: 'view_purchase_orders',
  CREATE_PURCHASE_ORDERS: 'create_purchase_orders',
  EDIT_PURCHASE_ORDERS: 'edit_purchase_orders',
  DELETE_PURCHASE_ORDERS: 'delete_purchase_orders',

  // Purchase Returns
  VIEW_PURCHASE_RETURNS: 'purchase-returns:read',
  CREATE_PURCHASE_RETURNS: 'purchase-returns:create',
  EDIT_PURCHASE_RETURNS: 'purchase-returns:update',
  APPROVE_PURCHASE_RETURNS: 'purchase-returns:approve',
  COMPLETE_PURCHASE_RETURNS: 'purchase-returns:complete',
  CANCEL_PURCHASE_RETURNS: 'purchase-returns:cancel',
  DELETE_PURCHASE_RETURNS: 'purchase-returns:delete',
  
  // POS
  ACCESS_POS: 'access_pos',
  VIEW_POS_HISTORY: 'view_pos_history',
  
  // Delivery Management
  VIEW_DELIVERIES: 'view_deliveries',
  MANAGE_DELIVERIES: 'manage_deliveries',
  // Backend "delivery:manage" (driver assignment, available drivers list).
  // Kept separate from MANAGE_DELIVERIES, which delivery:create/update also grant.
  ASSIGN_DELIVERY_DRIVERS: 'delivery:manage',

  // Returns
  VIEW_RETURNS: 'returns:read',
  CREATE_RETURNS: 'returns:create',
  MANAGE_RETURNS: 'returns:update',
  
  // Reports
  VIEW_REPORTS: 'view_reports',
  EXPORT_REPORTS: 'export_reports',
  
  // Coupons
  VIEW_COUPONS: 'view_coupons',
  CREATE_COUPONS: 'create_coupons',
  EDIT_COUPONS: 'edit_coupons',
  DELETE_COUPONS: 'delete_coupons',
  
  // Company Settings
  VIEW_SETTINGS: 'view_settings',
  EDIT_SETTINGS: 'edit_settings',

  // Employee Expenses
  VIEW_EMPLOYEE_EXPENSES: 'view_employee_expenses',
  CREATE_EMPLOYEE_EXPENSES: 'create_employee_expenses',
  EDIT_EMPLOYEE_EXPENSES: 'edit_employee_expenses',
  DELETE_EMPLOYEE_EXPENSES: 'delete_employee_expenses',

  VIEW_RATING: 'view_rating',

} as const;

export type Permission = typeof PERMISSIONS[keyof typeof PERMISSIONS];

/**
 * Route to required permissions mapping
 */
export const ROUTE_PERMISSIONS: Record<string, Permission[]> = {
  '/admin/dashboard': [PERMISSIONS.VIEW_DASHBOARD],
  '/admin/users': [PERMISSIONS.VIEW_USERS],
  '/admin/roles': [PERMISSIONS.VIEW_ROLES],
  '/admin/products': [PERMISSIONS.VIEW_PRODUCTS],
  '/admin/categories': [PERMISSIONS.VIEW_CATEGORIES],
  '/admin/orders': [PERMISSIONS.VIEW_ORDERS],
  '/admin/invoices': [PERMISSIONS.VIEW_INVOICES],
  '/admin/inventory': [PERMISSIONS.VIEW_INVENTORY],
  '/admin/purchase-orders': [PERMISSIONS.VIEW_PURCHASE_ORDERS],
  '/admin/purchase-returns': [PERMISSIONS.VIEW_PURCHASE_RETURNS],
  '/admin/pos': [PERMISSIONS.ACCESS_POS],
  '/admin/pos/history': [PERMISSIONS.VIEW_POS_HISTORY],
  '/admin/deliveries': [PERMISSIONS.VIEW_DELIVERIES],
  '/admin/returns': [PERMISSIONS.VIEW_RETURNS],
  '/admin/reports': [PERMISSIONS.VIEW_REPORTS],
  '/admin/coupon': [PERMISSIONS.VIEW_COUPONS],
  '/admin/company-settings': [PERMISSIONS.VIEW_SETTINGS],
};

const RESOURCE_ACTION_PERMISSION_MAP: Record<string, Record<string, string[]>> = {
  dashboard: {
    read: [PERMISSIONS.VIEW_DASHBOARD],
    view: [PERMISSIONS.VIEW_DASHBOARD],
  },
  users: {
    create: [PERMISSIONS.CREATE_USERS],
    read: [PERMISSIONS.VIEW_USERS],
    update: [PERMISSIONS.EDIT_USERS],
    delete: [PERMISSIONS.DELETE_USERS],
  },
  customers: {
    create: [PERMISSIONS.CREATE_USERS],
    read: [PERMISSIONS.VIEW_USERS],
    update: [PERMISSIONS.EDIT_USERS],
    delete: [PERMISSIONS.DELETE_USERS],
  },
  roles: {
    create: [PERMISSIONS.CREATE_ROLES],
    read: [PERMISSIONS.VIEW_ROLES],
    update: [PERMISSIONS.EDIT_ROLES],
    delete: [PERMISSIONS.DELETE_ROLES],
  },
  products: {
    create: [PERMISSIONS.CREATE_PRODUCTS],
    read: [PERMISSIONS.VIEW_PRODUCTS],
    update: [PERMISSIONS.EDIT_PRODUCTS],
    delete: [PERMISSIONS.DELETE_PRODUCTS],
  },
  categories: {
    create: [PERMISSIONS.CREATE_CATEGORIES],
    read: [PERMISSIONS.VIEW_CATEGORIES],
    update: [PERMISSIONS.EDIT_CATEGORIES],
    delete: [PERMISSIONS.DELETE_CATEGORIES],
  },
  orders: {
    create: [PERMISSIONS.CREATE_ORDERS],
    read: [PERMISSIONS.VIEW_ORDERS],
    update: [PERMISSIONS.EDIT_ORDERS],
    delete: [PERMISSIONS.DELETE_ORDERS],
  },
  invoices: {
    create: [PERMISSIONS.CREATE_INVOICES],
    read: [PERMISSIONS.VIEW_INVOICES],
    update: [PERMISSIONS.EDIT_INVOICES],
    delete: [PERMISSIONS.DELETE_INVOICES],
  },
  inventory: {
    read: [PERMISSIONS.VIEW_INVENTORY],
    create: [PERMISSIONS.MANAGE_INVENTORY],
    update: [PERMISSIONS.MANAGE_INVENTORY],
    delete: [PERMISSIONS.MANAGE_INVENTORY],
  },
  purchase_orders: {
    create: [PERMISSIONS.CREATE_PURCHASE_ORDERS],
    read: [PERMISSIONS.VIEW_PURCHASE_ORDERS],
    update: [PERMISSIONS.EDIT_PURCHASE_ORDERS],
    delete: [PERMISSIONS.DELETE_PURCHASE_ORDERS],
  },
  purchaseorders: {
    create: [PERMISSIONS.CREATE_PURCHASE_ORDERS],
    read: [PERMISSIONS.VIEW_PURCHASE_ORDERS],
    update: [PERMISSIONS.EDIT_PURCHASE_ORDERS],
    delete: [PERMISSIONS.DELETE_PURCHASE_ORDERS],
  },
  purchase_returns: {
    read: [PERMISSIONS.VIEW_PURCHASE_RETURNS],
    create: [PERMISSIONS.CREATE_PURCHASE_RETURNS],
    update: [PERMISSIONS.EDIT_PURCHASE_RETURNS],
    approve: [PERMISSIONS.APPROVE_PURCHASE_RETURNS],
    complete: [PERMISSIONS.COMPLETE_PURCHASE_RETURNS],
    cancel: [PERMISSIONS.CANCEL_PURCHASE_RETURNS],
    delete: [PERMISSIONS.DELETE_PURCHASE_RETURNS],
  },
  pos: {
    create: [PERMISSIONS.ACCESS_POS],
    read: [PERMISSIONS.ACCESS_POS],
    update: [PERMISSIONS.ACCESS_POS],
    delete: [PERMISSIONS.ACCESS_POS],
    history: [PERMISSIONS.VIEW_POS_HISTORY],
  },
  deliveries: {
    create: [PERMISSIONS.MANAGE_DELIVERIES],
    read: [PERMISSIONS.VIEW_DELIVERIES],
    manage: [PERMISSIONS.MANAGE_DELIVERIES, PERMISSIONS.ASSIGN_DELIVERY_DRIVERS],
    update: [PERMISSIONS.MANAGE_DELIVERIES],
    delete: [PERMISSIONS.MANAGE_DELIVERIES],
  },
  delivery: {
    create: [PERMISSIONS.MANAGE_DELIVERIES],
    read: [PERMISSIONS.VIEW_DELIVERIES],
    manage: [PERMISSIONS.MANAGE_DELIVERIES, PERMISSIONS.ASSIGN_DELIVERY_DRIVERS],
    update: [PERMISSIONS.MANAGE_DELIVERIES],
    delete: [PERMISSIONS.MANAGE_DELIVERIES],
  },
  returns: {
    read: [PERMISSIONS.VIEW_RETURNS],
    create: [PERMISSIONS.CREATE_RETURNS],
    update: [PERMISSIONS.MANAGE_RETURNS],
    manage: [PERMISSIONS.MANAGE_RETURNS],
  },
  return_requests: {
    read: [PERMISSIONS.VIEW_RETURNS],
    create: [PERMISSIONS.CREATE_RETURNS],
    update: [PERMISSIONS.MANAGE_RETURNS],
    manage: [PERMISSIONS.MANAGE_RETURNS],
  },
  reports: {
    read: [PERMISSIONS.VIEW_REPORTS],
    export: [PERMISSIONS.EXPORT_REPORTS],
  },
  coupons: {
    create: [PERMISSIONS.CREATE_COUPONS],
    read: [PERMISSIONS.VIEW_COUPONS],
    update: [PERMISSIONS.EDIT_COUPONS],
    delete: [PERMISSIONS.DELETE_COUPONS],
  },
  coupon: {
    create: [PERMISSIONS.CREATE_COUPONS],
    read: [PERMISSIONS.VIEW_COUPONS],
    update: [PERMISSIONS.EDIT_COUPONS],
    delete: [PERMISSIONS.DELETE_COUPONS],
  },
  settings: {
    read: [PERMISSIONS.VIEW_SETTINGS],
    update: [PERMISSIONS.EDIT_SETTINGS],
    edit: [PERMISSIONS.EDIT_SETTINGS],
  },
  company_settings: {
    read: [PERMISSIONS.VIEW_SETTINGS],
    update: [PERMISSIONS.EDIT_SETTINGS],
    edit: [PERMISSIONS.EDIT_SETTINGS],
  },
  expenses: {
    create: [PERMISSIONS.CREATE_EMPLOYEE_EXPENSES],
    read: [PERMISSIONS.VIEW_EMPLOYEE_EXPENSES],
    update: [PERMISSIONS.EDIT_EMPLOYEE_EXPENSES],
    delete: [PERMISSIONS.DELETE_EMPLOYEE_EXPENSES],
  },
  employee_expenses: {
    create: [PERMISSIONS.CREATE_EMPLOYEE_EXPENSES],
    read: [PERMISSIONS.VIEW_EMPLOYEE_EXPENSES],
    update: [PERMISSIONS.EDIT_EMPLOYEE_EXPENSES],
    delete: [PERMISSIONS.DELETE_EMPLOYEE_EXPENSES],
  },
  rating: {
    read: [PERMISSIONS.VIEW_RATING],
  },
  ratings: {
    read: [PERMISSIONS.VIEW_RATING],
  },
};

const normalizeResourceKey = (resource: string) =>
  resource
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const mapResourceActionToPermissions = (
  resource: string,
  action: string,
): string[] => {
  const normalizedResource = normalizeResourceKey(resource);
  const normalizedAction = String(action || "").trim().toLowerCase();

  return (
    RESOURCE_ACTION_PERMISSION_MAP[normalizedResource]?.[normalizedAction] || []
  );
};

// Enable/disable debug logging
const DEBUG_PERMISSIONS = false;

const debugLog = (message: string, data?: any) => {
  if (DEBUG_PERMISSIONS) {
    console.log(`[PERMISSIONS] ${message}`, data !== undefined ? data : '');
  }
};

/**
 * Normalize permissions from API response
 * Handles multiple formats:
 * 1. Flat array: ["permission1", "permission2"]
 * 2. Nested object: { "all": ["*"], "users": ["view_users"] }
 * 3. Direct object: { permissions: { "all": ["*"] } }
 * 4. null/undefined
 */
export const normalizePermissions = (
  permissions: any
): string[] => {
  debugLog('normalizePermissions input:', permissions);
  
  // Handle null/undefined
  if (!permissions) {
    debugLog('normalizePermissions: permissions is null/undefined');
    return [];
  }

  // If it's already an array, return it
  if (Array.isArray(permissions)) {
    const normalized = permissions
      .flatMap((permission) => {
        if (typeof permission === "string") {
          const trimmedPermission = permission.trim();
          return trimmedPermission
            ? [trimmedPermission, trimmedPermission.toLowerCase()]
            : [];
        }

        if (permission && typeof permission === "object") {
          return normalizePermissions(permission);
        }

        return [];
      })
      .filter(Boolean);

    const uniquePermissions = Array.from(new Set(normalized));
    debugLog('normalizePermissions: array format', uniquePermissions);
    return uniquePermissions;
  }

  // If it's an object, flatten all arrays from all keys
  if (typeof permissions === 'object') {
    const allPermissions = new Set<string>();
    
    // Handle nested permissions object
    if (permissions.permissions) {
      debugLog('normalizePermissions: has nested permissions property');
      return normalizePermissions(permissions.permissions);
    }
    
    Object.keys(permissions).forEach((key) => {
      const value = permissions[key];
      debugLog(`normalizePermissions: processing key "${key}"`, value);
      
      if (Array.isArray(value)) {
        value.forEach((permission) => {
          if (typeof permission !== "string") {
            if (permission && typeof permission === "object") {
              normalizePermissions(permission).forEach((nestedPermission) =>
                allPermissions.add(nestedPermission),
              );
            }
            return;
          }

          const trimmedPermission = permission.trim();
          if (!trimmedPermission) return;

          allPermissions.add(trimmedPermission);
          allPermissions.add(trimmedPermission.toLowerCase());
          mapResourceActionToPermissions(key, trimmedPermission).forEach(
            (mappedPermission) => allPermissions.add(mappedPermission),
          );
        });
      } else if (typeof value === 'string') {
        const trimmedValue = value.trim();
        if (trimmedValue) {
          allPermissions.add(trimmedValue);
          allPermissions.add(trimmedValue.toLowerCase());
          mapResourceActionToPermissions(key, trimmedValue).forEach(
            (mappedPermission) => allPermissions.add(mappedPermission),
          );
        }
      } else if (typeof value === 'object' && value !== null) {
        // Recursively handle nested objects
        normalizePermissions(value).forEach((nestedPermission) =>
          allPermissions.add(nestedPermission),
        );
      }
    });
    
    const flattenedPermissions = Array.from(allPermissions);
    debugLog('normalizePermissions: flattened result', flattenedPermissions);
    return flattenedPermissions;
  }

  // If it's a string, return as single-item array
  if (typeof permissions === 'string') {
    const trimmedPermission = permissions.trim();
    const normalized = trimmedPermission
      ? Array.from(new Set([trimmedPermission, trimmedPermission.toLowerCase()]))
      : [];
    debugLog('normalizePermissions: string format', normalized);
    return normalized;
  }

  debugLog('normalizePermissions: unknown format, returning empty array');
  return [];
};

/**
 * Check if user has wildcard/all permissions
 * Recognizes: *, all, ALL, administrator, admin
 */
export const hasWildcardPermission = (
  userPermissions: any
): boolean => {
  const normalizedPermissions = normalizePermissions(userPermissions);
  
  debugLog('hasWildcardPermission check:', normalizedPermissions);
  
  if (normalizedPermissions.length === 0) {
    debugLog('hasWildcardPermission: no permissions');
    return false;
  }
  
  // Check for various wildcard formats
  const wildcardPatterns = ['*', 'all', 'ALL', 'administrator', 'admin', 'superadmin', 'super_admin'];
  const hasWildcard = wildcardPatterns.some(pattern => 
    normalizedPermissions.includes(pattern)
  );
  
  debugLog('hasWildcardPermission result:', hasWildcard);
  return hasWildcard;
};

/**
 * Check if user has a specific permission
 * Also checks for wildcard permissions (*, all, etc.)
 */
export const hasPermission = (
  userPermissions: any,
  requiredPermission: Permission
): boolean => {
  debugLog('hasPermission check:', { 
    userPermissions, 
    requiredPermission 
  });
  
  const normalizedPermissions = normalizePermissions(userPermissions);
  
  if (normalizedPermissions.length === 0) {
    debugLog('hasPermission: no permissions, access denied');
    return false;
  }
  
  // Check for wildcard permissions first
  if (hasWildcardPermission(userPermissions)) {
    debugLog('hasPermission: has wildcard, access granted');
    return true;
  }
  
  const hasAccess = normalizedPermissions.includes(requiredPermission);
  debugLog('hasPermission result:', hasAccess);
  
  return hasAccess;
};

/**
 * Check if user has any of the required permissions
 * Also checks for wildcard permissions (*, all, etc.)
 */
export const hasAnyPermission = (
  userPermissions: any,
  requiredPermissions: Permission[]
): boolean => {
  debugLog('hasAnyPermission check:', { 
    userPermissions, 
    requiredPermissions 
  });
  
  const normalizedPermissions = normalizePermissions(userPermissions);
  
  if (normalizedPermissions.length === 0) {
    debugLog('hasAnyPermission: no permissions, access denied');
    return false;
  }
  
  // Check for wildcard permissions first
  if (hasWildcardPermission(userPermissions)) {
    debugLog('hasAnyPermission: has wildcard, access granted');
    return true;
  }
  
  const hasAccess = requiredPermissions.some(permission => 
    normalizedPermissions.includes(permission)
  );
  
  debugLog('hasAnyPermission result:', hasAccess);
  debugLog('hasAnyPermission details:', {
    normalizedPermissions,
    requiredPermissions,
    matches: requiredPermissions.filter(p => normalizedPermissions.includes(p))
  });
  
  return hasAccess;
};

/**
 * Check if user has all of the required permissions
 * Also checks for wildcard permissions (*, all, etc.)
 */
export const hasAllPermissions = (
  userPermissions: any,
  requiredPermissions: Permission[]
): boolean => {
  debugLog('hasAllPermissions check:', { 
    userPermissions, 
    requiredPermissions 
  });
  
  const normalizedPermissions = normalizePermissions(userPermissions);
  
  if (normalizedPermissions.length === 0) {
    debugLog('hasAllPermissions: no permissions, access denied');
    return false;
  }
  
  // Check for wildcard permissions first
  if (hasWildcardPermission(userPermissions)) {
    debugLog('hasAllPermissions: has wildcard, access granted');
    return true;
  }
  
  const hasAccess = requiredPermissions.every(permission => 
    normalizedPermissions.includes(permission)
  );
  
  debugLog('hasAllPermissions result:', hasAccess);
  
  return hasAccess;
};

/**
 * Check if user can access a specific route
 * Also checks for wildcard permissions (*, all, etc.)
 */
export const canAccessRoute = (
  userPermissions: any,
  route: string
): boolean => {
  debugLog('canAccessRoute check:', { userPermissions, route });
  
  // Check for wildcard permissions first
  if (hasWildcardPermission(userPermissions)) {
    debugLog('canAccessRoute: has wildcard, access granted');
    return true;
  }
  
  const requiredPermissions = ROUTE_PERMISSIONS[route];
  
  // If route has no permission requirements, allow access
  if (!requiredPermissions || requiredPermissions.length === 0) {
    debugLog('canAccessRoute: no permissions required, access granted');
    return true;
  }
  
  // Check if user has any of the required permissions
  const hasAccess = hasAnyPermission(userPermissions, requiredPermissions);
  debugLog('canAccessRoute result:', hasAccess);
  
  return hasAccess;
};

/**
 * Get missing permissions for a route
 */
export const getMissingPermissions = (
  userPermissions: any,
  route: string
): Permission[] => {
  // If user has wildcard, they have no missing permissions
  if (hasWildcardPermission(userPermissions)) {
    return [];
  }
  
  const normalizedPermissions = normalizePermissions(userPermissions);
  const requiredPermissions = ROUTE_PERMISSIONS[route];
  
  if (!requiredPermissions || requiredPermissions.length === 0) {
    return [];
  }
  
  if (normalizedPermissions.length === 0) {
    return requiredPermissions;
  }
  
  return requiredPermissions.filter(
    permission => !normalizedPermissions.includes(permission)
  );
};

/**
 * Filter navigation items based on user permissions
 */
export const filterNavigationByPermissions = <T extends { path: string }>(
  items: T[],
  userPermissions: any
): T[] => {
  return items.filter(item => canAccessRoute(userPermissions, item.path));
};

/**
 * Debug helper - prints permission status
 */
export const debugPermissions = (userPermissions: any, route?: string) => {
  console.group('🔍 Permission Debug Info');
  console.log('Raw permissions:', userPermissions);
  console.log('Normalized permissions:', normalizePermissions(userPermissions));
  console.log('Has wildcard?', hasWildcardPermission(userPermissions));
  
  if (route) {
    console.log('Route:', route);
    console.log('Required permissions:', ROUTE_PERMISSIONS[route]);
    console.log('Can access route?', canAccessRoute(userPermissions, route));
    console.log('Missing permissions:', getMissingPermissions(userPermissions, route));
  }
  
  console.groupEnd();
};
